import * as React from "react";
import { Button } from "../../../components/ui/button";
import { EditInfo } from "../../../platform";
import EditCard from "./EditCard";

interface EditCardListProps {
  edits: EditInfo[];
  messageIndex: number;
  /** The edit the document is currently showing, if any. */
  activeEditIndex?: number | null;
  canReview: boolean;
  /** Whether this host can move to an edit at all. False in Outlook. */
  canNavigate: boolean;
  onJumpToEdit: (messageIndex: number, editIndex: number) => void;
  onAcceptEdit: (messageIndex: number, editIndex: number) => void;
  onRejectEdit: (messageIndex: number, editIndex: number) => void;
  onAcceptAll: (messageIndex: number) => void;
  onRejectAll: (messageIndex: number) => void;
}

const EditCardList: React.FC<EditCardListProps> = ({
  edits,
  messageIndex,
  activeEditIndex,
  canReview,
  canNavigate,
  onJumpToEdit,
  onAcceptEdit,
  onRejectEdit,
  onAcceptAll,
  onRejectAll,
}) => {
  // "Accept all" needs MORE THAN ONE edit that can actually be accepted. An edit with no
  // anchor cannot be -- a Word comment, or anything at all in Excel and PowerPoint --
  // so the bulk controls key off that rather than off a list of tool names the shared
  // layer would have to keep in step with three hosts.
  //
  // The count, not merely "any", because a lone edit already has its own Accept and
  // Reject on the card: offering Accept All beside them is two controls for one action,
  // and the bulk pair reads as though it would do something wider than it does.
  const reviewableCount = canReview
    ? edits.filter((edit) => edit.status === "pending" && edit.bookmarkName).length
    : 0;
  const anyReviewable = reviewableCount > 1;

  return (
    <div className="flex w-full flex-col gap-2">
      {edits.map((edit, index) => (
        <EditCard
          key={`edit-${index}`}
          edit={edit}
          editIndex={index}
          messageIndex={messageIndex}
          isActive={index === activeEditIndex}
          canReview={canReview}
          canNavigate={canNavigate}
          onJumpToEdit={onJumpToEdit}
          onAcceptEdit={onAcceptEdit}
          onRejectEdit={onRejectEdit}
        />
      ))}
      {anyReviewable && (
        <div className="flex justify-end gap-1">
          <Button
            variant="ghost"
            onClick={() => onRejectAll(messageIndex)}
            className="text-muted-foreground hover:text-muted-foreground"
          >
            Reject all
          </Button>
          {/* Outlined, not filled: the per-card Accept is the primary action and this
              is the shortcut for taking them all, so it sits one step down. */}
          <Button variant="outline" onClick={() => onAcceptAll(messageIndex)}>
            Accept all ({reviewableCount})
          </Button>
        </div>
      )}
    </div>
  );
};

export default EditCardList;
